import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, UsersRound } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/common/searchable-select'
import { getErrorMessage } from '@/lib/api'
import crewsApi from '@/services/crews'
import { jobsApi } from '@/services/jobs'
import type { Job } from '@/types'

type JobTeamAssignmentControlsProps = {
  job: Job
  serviceInstanceId?: string | null
  onApplied?: () => void
}

export function JobTeamAssignmentControls({
  job,
  serviceInstanceId,
  onApplied,
}: JobTeamAssignmentControlsProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('none')
  const [selectedStaffedTeamId, setSelectedStaffedTeamId] = useState('none')

  useEffect(() => {
    if (!open) {
      return
    }

    setSelectedTemplateId(String(job.team_template ?? job.team_template_info?.id ?? 'none'))
    setSelectedStaffedTeamId('none')
  }, [open, job.id, job.team_template, job.team_template_info?.id])

  const teamTemplatesQuery = useQuery({
    queryKey: ['job-team-templates', serviceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listTemplates({
      service_instance_id: serviceInstanceId ?? undefined,
      active: true,
      page_size: 200,
    }),
    enabled: open && Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const staffedTeamsQuery = useQuery({
    queryKey: ['job-staffed-teams', serviceInstanceId ?? 'none', selectedTemplateId],
    queryFn: () => crewsApi.listReadyCrews({
      crew_template_id: selectedTemplateId,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: open && Boolean(serviceInstanceId && selectedTemplateId && selectedTemplateId !== 'none'),
    staleTime: 30_000,
  })

  const teamTemplateOptions = useMemo(
    () => [
      { value: 'none', label: 'Select team template' },
      ...(teamTemplatesQuery.data ?? []).map((template) => ({
        value: String(template.id),
        label: template.name || `Template #${template.id}`,
      })),
    ],
    [teamTemplatesQuery.data],
  )

  const staffedTeamOptions = useMemo(
    () => [
      { value: 'none', label: 'None (slots only)' },
      ...(staffedTeamsQuery.data ?? []).map((crew) => ({
        value: String(crew.id),
        label: crew.name || `Team #${crew.id}`,
      })),
    ],
    [staffedTeamsQuery.data],
  )

  const applyMutation = useMutation({
    mutationFn: async ({ templateId, staffedTeamId }: { templateId: string; staffedTeamId: string | null }) => {
      await jobsApi.update(String(job.id), {
        title: job.title,
        description: job.description ?? '',
        category: job.category,
        status: job.status,
        team_template: templateId,
      })

      if (staffedTeamId) {
        return crewsApi.assignToJob({ job_id: job.id, crew_id: staffedTeamId })
      }

      return null
    },
    onSuccess: async (assignResult) => {
      await queryClient.invalidateQueries({ queryKey: ['job', String(job.id)] })
      await queryClient.invalidateQueries({ queryKey: ['job-assignments', String(job.id)] })
      await queryClient.invalidateQueries({ queryKey: ['assignments'] })

      if (assignResult?.assigned_count) {
        toast.success(`Team added. ${assignResult.assigned_count} worker${assignResult.assigned_count === 1 ? '' : 's'} assigned to open slots.`)
      } else {
        toast.success('Team template applied. Missing role slots were added.')
      }

      setOpen(false)
      onApplied?.()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleApply = () => {
    if (!selectedTemplateId || selectedTemplateId === 'none') {
      toast.error('Select a team template.')
      return
    }

    applyMutation.mutate({
      templateId: selectedTemplateId,
      staffedTeamId: selectedStaffedTeamId && selectedStaffedTeamId !== 'none' ? selectedStaffedTeamId : null,
    })
  }

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value)
    setSelectedStaffedTeamId('none')
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full rounded-lg"
        disabled={!serviceInstanceId}
        onClick={() => setOpen(true)}
      >
        <UsersRound className="mr-2 h-4 w-4" />
        Add team
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add team</DialogTitle>
            <DialogDescription>
              Apply a team template to add missing role slots. Optionally assign a staffed team to fill open slots without replacing workers already on the job.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="add-team-template">Team template</Label>
              <SearchableSelect
                id="add-team-template"
                ariaLabel="Team template"
                value={selectedTemplateId}
                onValueChange={handleTemplateChange}
                options={teamTemplateOptions}
                placeholder={teamTemplatesQuery.isLoading ? 'Loading templates...' : 'Select team template'}
                searchPlaceholder="Search team templates"
                emptyText="No team templates found."
                disabled={!serviceInstanceId || teamTemplatesQuery.isLoading || applyMutation.isPending}
              />
            </div>

            {selectedTemplateId !== 'none' ? (
              <div className="space-y-2">
                <Label htmlFor="add-team-staffed">Staffed team</Label>
                <SearchableSelect
                  id="add-team-staffed"
                  ariaLabel="Staffed team"
                  value={selectedStaffedTeamId}
                  onValueChange={setSelectedStaffedTeamId}
                  options={staffedTeamOptions}
                  placeholder={staffedTeamsQuery.isLoading ? 'Loading teams...' : 'Select staffed team (optional)'}
                  searchPlaceholder="Search staffed teams"
                  emptyText="No ready teams for this template."
                  disabled={staffedTeamsQuery.isLoading || applyMutation.isPending}
                />
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={applyMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              disabled={selectedTemplateId === 'none' || applyMutation.isPending}
            >
              {applyMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default JobTeamAssignmentControls
